import React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

function getLowerPath(urlOrPath) {
  if (!urlOrPath) return "";
  try {
    return new URL(urlOrPath).pathname.toLowerCase();
  } catch {
    return String(urlOrPath).toLowerCase();
  }
}

function inferFileType({ url, fileName }) {
  const nameLower = (fileName || "").toLowerCase();
  const pathLower = getLowerPath(url);

  // If the display name does not contain an extension (e.g. it's just a label
  // like "Section 80D - Medical Insurance"), fall back to using the URL path
  // so we can still correctly infer the file type from the real object key.
  const lower = nameLower.includes(".") ? nameLower : pathLower;

  if (lower.endsWith(".pdf")) return "pdf";
  if (lower.endsWith(".ppt") || lower.endsWith(".pptx")) return "ppt";
  if (/\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(lower)) return "image";

  return "other";
}

export default function AttachmentPreviewModal({
  isOpen,
  url,
  downloadUrl,
  fileName,
  onClose,
}) {
  if (!isOpen) return null;
  if (typeof document === "undefined") return null;

  const type = inferFileType({ url, fileName });

  const officeViewerUrl =
    type === "ppt" && url
      ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
          url
        )}`
      : null;

  const resolvedDownloadUrl = downloadUrl || url;

  // Portal + high z-index so sticky chrome (navbar / module nav) cannot overlap the preview.
  return createPortal(
    <div className="fixed inset-0 z-[100050] flex items-center justify-center bg-black/50 p-4">
      <div
        className="max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-lg bg-white p-6 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label={fileName || "Attachment preview"}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between gap-3">
          <h3 className="truncate text-lg font-semibold">{fileName}</h3>
          <button
            onClick={onClose}
            className="shrink-0 text-gray-500 hover:text-gray-700"
            type="button"
            aria-label="Close preview"
          >
            <X size={24} />
          </button>
        </div>

        <div className="mb-4 max-h-[70vh] overflow-auto">
          {url ? (
            (() => {
              if (type === "pdf") {
                return (
                  <iframe
                    key={url}
                    src={url}
                    title={fileName}
                    className="h-[500px] w-full border-0"
                  />
                );
              }

              if (type === "ppt") {
                return (
                  <div className="h-[500px] w-full">
                    <iframe
                      key={officeViewerUrl}
                      src={officeViewerUrl}
                      title={fileName}
                      className="h-[500px] w-full border-0"
                    />
                  </div>
                );
              }

              if (type === "image") {
                return (
                  <img
                    src={url}
                    alt={fileName}
                    className="mx-auto max-h-[500px] max-w-full"
                  />
                );
              }

              return (
                <div className="flex h-[500px] flex-col items-center justify-center p-8">
                  <div className="mb-4 text-gray-500">
                    Preview not available for this file type
                  </div>
                  <div className="flex gap-2">
                    <a
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded bg-gray-200 px-4 py-2 text-gray-800 hover:bg-gray-300"
                    >
                      Open
                    </a>
                    <a
                      href={resolvedDownloadUrl}
                      download={fileName}
                      className="rounded bg-[hsl(var(--ds-primary))] px-4 py-2 text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))]"
                    >
                      Download File
                    </a>
                  </div>
                </div>
              );
            })()
          ) : (
            <div className="flex h-[500px] items-center justify-center">
              <div className="text-gray-500">Loading...</div>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          {url && (
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="mr-2 rounded bg-gray-200 px-4 py-2 text-gray-800 hover:bg-gray-300"
            >
              Open in new tab
            </a>
          )}
          {resolvedDownloadUrl && (
            <a
              href={resolvedDownloadUrl}
              download={fileName}
              className="mr-2 rounded bg-[hsl(var(--ds-primary))] px-4 py-2 text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))]"
            >
              Download
            </a>
          )}
          <button
            onClick={onClose}
            className="rounded border border-gray-300 px-4 py-2 hover:bg-gray-50"
            type="button"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
